import { useEffect, useRef, useState } from 'react'
import { popularPlaces, searchPopular, type Place } from '../../../../shared/places.ts'
import { api } from '../../lib/api'
import { haptic } from '../../lib/telegram'
import { useSirr } from '../../store'

interface Props {
  value: Place | null
  onChange: (place: Place) => void
}

/**
 * Поиск города рождения. Пока пользователь не начал печатать — показываем
 * подсказки по целевому рынку; дальше спрашиваем геокодер через свой сервер.
 * Локальные совпадения показываются сразу, не дожидаясь сети.
 */
export function PlacePicker({ value, onChange }: Props) {
  const { lang, t } = useSirr()

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<Place[]>(() => popularPlaces(lang))
  const [loading, setLoading] = useState(false)
  const [degraded, setDegraded] = useState(false)

  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * Открывать список надо и по onFocus, и по onClick: после выбора города поле
   * остаётся сфокусированным, и повторный тап по нему onFocus уже не вызывает.
   */
  const openList = () => {
    if (open) return
    setOpen(true)
    setQuery('')
  }

  // Поиск с задержкой: без неё запрос уходил бы на каждое нажатие клавиши.
  useEffect(() => {
    if (!open) return

    const q = query.trim()
    // Локальные совпадения показываем немедленно, чтобы список не пустовал.
    setResults(searchPopular(q, lang))
    if (q.length < 2) {
      setDegraded(false)
      return
    }

    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      api
        .places(q, lang, ctrl.signal)
        .then((res) => {
          setResults(res.places)
          setDegraded(res.degraded)
        })
        .catch(() => {
          // Сеть недоступна — остаёмся на локальных совпадениях.
          setDegraded(true)
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false)
        })
    }, 350)

    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [query, lang, open])

  // Закрытие по тапу вне списка.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const pick = (place: Place) => {
    haptic.select()
    onChange(place)
    setQuery('')
    setOpen(false)
    // Снимаем фокус, чтобы на телефоне убралась клавиатура.
    inputRef.current?.blur()
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <div style={{ border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px' }}>
        <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 4 }}>{t('birthPlace')}</div>

        <input
          ref={inputRef}
          type="text"
          value={open ? query : (value ? value.name : '')}
          placeholder={value ? value.name : t('placeSearch')}
          onFocus={openList}
          onClick={openList}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: '100%',
            background: 'transparent',
            border: 0,
            outline: 'none',
            color: 'var(--ink)',
            fontFamily: 'var(--serif)',
            fontStyle: 'italic',
            fontSize: 17,
            padding: 0,
          }}
        />

        {!open && value?.region && (
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 3 }}>{value.region}</div>
        )}
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 20,
            maxHeight: 260,
            overflowY: 'auto',
            border: '1px solid rgba(200,162,78,.35)',
            borderRadius: 14,
            background: 'var(--bg-velvet)',
            boxShadow: '0 18px 40px rgba(0,0,0,.6)',
          }}
        >
          {loading && (
            <div style={{ padding: '10px 14px' }}>
              <div className="shimmer" style={{ width: '60%' }} />
            </div>
          )}

          {results.length === 0 && !loading && (
            <div style={{ padding: '14px', fontSize: 13, color: 'var(--ink-faint)', textAlign: 'center' }}>
              {t('placeNotFound')}
            </div>
          )}

          {results.map((place, i) => (
            <button
              key={`${place.name}-${place.latitude}-${place.longitude}-${i}`}
              type="button"
              onClick={() => pick(place)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '11px 14px',
                borderTop: i === 0 ? 0 : '1px solid rgba(200,162,78,.12)',
              }}
            >
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 16 }}>{place.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 2 }}>
                {place.region ? `${place.region} · ` : ''}
                {place.timeZone}
              </div>
            </button>
          ))}

          {degraded && (
            <div
              style={{
                padding: '10px 14px',
                fontSize: 11,
                color: 'var(--ink-faint)',
                borderTop: '1px solid rgba(200,162,78,.12)',
                lineHeight: 1.4,
              }}
            >
              {t('placeOffline')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
